import React, { useContext, useState, useEffect, useMemo } from 'react';
import cx from 'classnames';
import { FiChevronDown } from 'react-icons/fi';
import { isEmpty, isObject } from 'lodash';

import withControlled from '../form/with-controlled';

import Dropdown from '~components/dropdown';

import './style.scss';

const ValueContext = React.createContext();

function Container({
  children,
  className,
  isLoading,
  onChange,
  placeholder = 'Select',
  offset = 0,
  placement = 'bottom',
  defaultValue,
  inputRef,
  optionsClassName,
  value,
  disabled,
  errors,
  isInvalid,
  isVisible = false,
  ...props
}) {
  const [visible, setVisible] = useState(isVisible);
  const [label, setLabel] = useState(placeholder);
  const [popperPlacement, setPopperPlacement] = useState(placement);

  const modifiers = useMemo(
    () => [
      {
        name: 'offset',
        enabled: true,
        options: {
          offset: [0, offset],
        },
      },
      {
        name: 'sameWidth',
        enabled: true,
        fn: ({ state }) => {
          state.styles.popper.width = `${state.rects.reference.width}px`;
        },
        phase: 'beforeWrite',
        requires: ['computeStyles'],
      },
    ],
    []
  );

  const handleValueChange = (newValue, children) => {
    setLabel(children);
    onChange(newValue);
    setVisible(false);
  };

  const handleLabelChange = (newLabel) => {
    setLabel(newLabel);
  };

  useEffect(() => {
    if (value === null || value === undefined || value === '' || (isObject(value) && isEmpty(value))) {
      setLabel(placeholder);
    }
  }, [value]);

  return (
    <ValueContext.Provider value={{ onValueChange: handleValueChange, onLabelChange: handleLabelChange, value }}>
      <Dropdown.Container
        className={cx('c-combobox', `c-combobox--placement-${popperPlacement}`, {
          'c-combobox--loading': isLoading,
          'c-combobox--placeholder': value === null || value === undefined || value === '' || (isObject(value) && isEmpty(value)),
          'c-combobox--opened': visible,
          'c-combobox--invalid': isInvalid && !disabled,
          [className]: !!className,
        })}
        placement={placement}
        modifiers={modifiers}
        visible={visible}
        onVisibleChange={setVisible}
        optionsClassName={cx('c-combobox__options', {
          [optionsClassName]: !!optionsClassName,
        })}
        menuRole="listbox"
        onPopperMove={(popper) => {
          setPopperPlacement(popper && popper['data-popper-placement'] ? popper['data-popper-placement'] : placement);
        }}
      >
        <Dropdown.Toggle>
          {label}
          <FiChevronDown
            className={cx('c-combobox__chevron-down', {
              active: visible,
            })}
            aria-hidden="true"
          />
        </Dropdown.Toggle>
        <Dropdown.Content>{children}</Dropdown.Content>
      </Dropdown.Container>
      {errors && !disabled ? <div className="c-combobox__error">{errors.message}</div> : <></>}
    </ValueContext.Provider>
  );
}

function Option({ className, children, ...props }) {
  const { value, onValueChange, onLabelChange } = useContext(ValueContext);

  const handleClick = () => {
    onValueChange(props.value, children);
  };

  useEffect(() => {
    if (value === props.value) {
      onLabelChange(children);
    }
  }, [value]);

  return (
    <button
      type="button"
      className={cx('c-combobox__option', {
        [className]: !!className,
        'c-combobox__option--selected': value === props.value,
      })}
      onClick={handleClick}
      role="option"
      aria-selected={value === props.value}
    >
      {children}
    </button>
  );
}

const Controlled = withControlled(Container);

export default { Container, Option, Controlled };
